/* eslint-disable react-refresh/only-export-components --
 * Shared form module: the field grid plus its form-state type, empty-form
 * constant, and option lists are intentionally co-located.
 */
import React, { useRef, useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import {
  Checkbox,
  cn,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  DatePicker,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import type {
  AgencyInsuranceResponse,
  InsurancePosition,
  RelationToInsured,
} from '../../services/api/types.gen';
import { formatSSN } from '../../utils/intake';
import { GENDER_OPTIONS, US_STATES } from '../../constants/options';

// ─── Constants ───────────────────────────────────────────────────────────────

export const POSITION_OPTIONS: { value: InsurancePosition; label: string }[] = [
  { value: 'Primary', label: 'Primary' },
  { value: 'Secondary', label: 'Secondary' },
  { value: 'Tertiary', label: 'Tertiary' },
  { value: 'Quaternary', label: 'Quaternary' },
];

/** Shown under the disabled insurance picker when editing an existing policy. */
export const INSURANCE_LOCKED_MESSAGE =
  'Insurance cannot be changed on an existing policy. To use a different insurance, add a new insurance entry.';

export const RELATION_OPTIONS: { value: RelationToInsured; label: string }[] = [
  { value: 'Self', label: 'Self' },
  { value: 'Spouse', label: 'Spouse' },
  { value: 'Child', label: 'Child' },
  { value: 'Other', label: 'Other' },
];

// ─── Types ────────────────────────────────────────────────────────────────────

export type PatientInsuranceFormState = {
  agency_insurance_id: string;
  position: InsurancePosition | '';
  insured_last_name: string;
  insured_first_name: string;
  insured_gender: string;
  insured_ssn: string;
  insured_dob: Date | undefined;
  insured_phone: string;
  insured_employer: string;
  plan_name: string;
  insured_address: string;
  insured_city: string;
  insured_state: string;
  insured_zip: string;
  relation_to_insured: RelationToInsured | '';
  group_name: string;
  group_number: string;
  policy_number: string;
  effective_start: Date | undefined;
  effective_end: Date | undefined;
  assignment_of_benefits: boolean;
  deductible: string;
  coverage_percent: string;
  copay_amount: string;
  insured_signature_on_file: boolean;
};

export const EMPTY_PATIENT_INSURANCE_FORM: PatientInsuranceFormState = {
  agency_insurance_id: '',
  position: 'Primary',
  insured_last_name: '',
  insured_first_name: '',
  insured_gender: '',
  insured_ssn: '',
  insured_dob: undefined,
  insured_phone: '',
  insured_employer: '',
  plan_name: '',
  insured_address: '',
  insured_city: '',
  insured_state: '',
  insured_zip: '',
  relation_to_insured: '',
  group_name: '',
  group_number: '',
  policy_number: '',
  effective_start: undefined,
  effective_end: undefined,
  assignment_of_benefits: true,
  deductible: '',
  coverage_percent: '',
  copay_amount: '',
  insured_signature_on_file: true,
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

export function parseOptionalFloat(val: string): number | null {
  if (!val.trim()) return null;
  const n = parseFloat(val);
  return isNaN(n) ? null : n;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const FieldLabel: React.FC<{
  children: React.ReactNode;
  required?: boolean;
  error?: boolean;
}> = ({ children, required, error }) => (
  <Label
    className={cn(
      'text-xs font-medium',
      error ? 'text-red-500' : 'text-muted-foreground'
    )}
  >
    {children}
    {required && (
      <span
        className={cn('ml-0.5', error ? 'text-red-500' : 'text-destructive')}
      >
        *
      </span>
    )}
  </Label>
);

const StateCombobox: React.FC<{
  value: string;
  onChange: (v: string) => void;
  error?: boolean;
}> = ({ value, onChange, error }) => {
  const [open, setOpen] = useState(false);
  const selected = US_STATES.find(s => s.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type='button'
          role='combobox'
          aria-expanded={open}
          className={cn(
            'flex h-7 w-full items-center justify-between rounded-md border border-input bg-background px-2 text-xs ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
            error && 'border-red-500 focus:ring-red-500',
            !value && 'text-muted-foreground'
          )}
        >
          <span className='truncate'>
            {selected
              ? `${selected.label} (${selected.value})`
              : 'Select state'}
          </span>
          <ChevronsUpDown className='ml-1 h-3.5 w-3.5 shrink-0 opacity-50' />
        </button>
      </PopoverTrigger>
      <PopoverContent className='w-56 p-0' align='start'>
        <Command>
          <CommandInput placeholder='Search states…' className='h-8 text-xs' />
          <CommandList>
            <CommandEmpty className='py-3 text-center text-xs text-muted-foreground'>
              No state found.
            </CommandEmpty>
            <CommandGroup>
              {US_STATES.map(st => (
                <CommandItem
                  key={st.value}
                  value={st.label}
                  onSelect={() => {
                    onChange(st.value);
                    setOpen(false);
                  }}
                  className='text-xs'
                >
                  <Check
                    className={cn(
                      'mr-2 h-3.5 w-3.5',
                      value === st.value ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  {st.label} ({st.value})
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const InsuranceCombobox: React.FC<{
  value: string;
  onChange: (v: string) => void;
  options: AgencyInsuranceResponse[];
  error?: boolean;
  /** Greys the picker out; the parent renders the reason under it. */
  disabled?: boolean;
}> = ({ value, onChange, options, error, disabled }) => {
  const [open, setOpen] = useState(false);
  const listRef = useRef<HTMLDivElement | null>(null);
  const selected = options.find(ai => ai.id === value);

  const handleListWheel = (event: React.WheelEvent<HTMLDivElement>) => {
    const listElement = listRef.current;
    if (!listElement || listElement.scrollHeight <= listElement.clientHeight) {
      return;
    }

    listElement.scrollTop += event.deltaY;
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <Popover modal={false} open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type='button'
          role='combobox'
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'flex h-7 w-full items-center justify-between rounded-md border border-input bg-background px-2 text-xs ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
            error && 'border-red-500 focus:ring-red-500',
            !value && 'text-muted-foreground'
          )}
        >
          <span className='truncate'>
            {selected ? selected.name : 'Select insurance'}
          </span>
          <ChevronsUpDown className='ml-1 h-3.5 w-3.5 shrink-0 opacity-50' />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className='w-[var(--radix-popover-trigger-width)] p-0'
        align='start'
      >
        <Command>
          <CommandInput
            placeholder='Search insurance…'
            className='h-8 text-xs'
          />
          <CommandList
            ref={listRef}
            className='max-h-60 overscroll-contain'
            onWheelCapture={handleListWheel}
          >
            <CommandEmpty className='py-3 text-center text-xs text-muted-foreground'>
              No insurance found.
            </CommandEmpty>
            <CommandGroup>
              {options.map(ai => (
                <CommandItem
                  key={ai.id}
                  value={ai.name}
                  onSelect={() => {
                    onChange(ai.id);
                    setOpen(false);
                  }}
                  className='text-xs'
                >
                  <Check
                    className={cn(
                      'mr-2 h-3.5 w-3.5',
                      value === ai.id ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  {ai.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

export interface PatientInsuranceFieldsProps {
  form: PatientInsuranceFormState;
  onFieldChange: <K extends keyof PatientInsuranceFormState>(
    key: K,
    value: PatientInsuranceFormState[K]
  ) => void;
  onFieldBlur?: (key: keyof PatientInsuranceFormState) => void;
  errors?: ReadonlySet<string>;
  /** Only needed when the insurance picker is visible (not in hiddenFields). */
  agencyInsurances?: AgencyInsuranceResponse[];
  /**
   * Disable the insurance picker and show this reason under it. The payer of a
   * policy is fixed once it exists (the API refuses a change), so the edit
   * dialog passes this. A visible line, not a tooltip, so phones see it too.
   */
  insuranceLockedMessage?: string;
  /** Fields to omit entirely (e.g. intake binds payer + member ID elsewhere). */
  hiddenFields?: ReadonlyArray<keyof PatientInsuranceFormState>;
  /** Prefix for checkbox DOM ids so multiple instances don't collide. */
  idPrefix?: string;
}

/**
 * Presentational patient-insurance field grid shared by the Patient Manager
 * insurance dialog and the intake Eligibility tab section. State ownership,
 * validation, and persistence stay with the parent.
 */
export const PatientInsuranceFields: React.FC<PatientInsuranceFieldsProps> = ({
  form,
  onFieldChange,
  onFieldBlur,
  errors,
  agencyInsurances = [],
  insuranceLockedMessage,
  hiddenFields,
  idPrefix = '',
}) => {
  const hidden = new Set<keyof PatientInsuranceFormState>(hiddenFields ?? []);
  const hasError = (key: string) => errors?.has(key) ?? false;

  return (
    <div className='space-y-5'>
      {/* Insurance & Position */}
      {(!hidden.has('agency_insurance_id') || !hidden.has('position')) && (
        <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
          {!hidden.has('agency_insurance_id') && (
            <div className='space-y-1.5'>
              <FieldLabel required error={hasError('agency_insurance_id')}>
                Insurance
              </FieldLabel>
              <InsuranceCombobox
                value={form.agency_insurance_id}
                onChange={v => onFieldChange('agency_insurance_id', v)}
                options={agencyInsurances}
                error={hasError('agency_insurance_id')}
                disabled={insuranceLockedMessage !== undefined}
              />
              {insuranceLockedMessage && (
                <p className='text-xs text-muted-foreground'>
                  {insuranceLockedMessage}
                </p>
              )}
            </div>
          )}
          {!hidden.has('position') && (
            <div className='space-y-1.5'>
              <FieldLabel required error={hasError('position')}>
                Position
              </FieldLabel>
              <Select
                value={form.position}
                onValueChange={v =>
                  onFieldChange('position', v as InsurancePosition)
                }
              >
                <SelectTrigger
                  selectSize='sm'
                  className={
                    hasError('position')
                      ? 'border-red-500 focus:ring-red-500'
                      : undefined
                  }
                >
                  <SelectValue placeholder='Select position' />
                </SelectTrigger>
                <SelectContent>
                  {POSITION_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      )}

      {/* Insured name / gender / SSN / DOB */}
      <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
        <div className='space-y-1.5'>
          <FieldLabel>Insured Last Name</FieldLabel>
          <Input
            value={form.insured_last_name}
            onChange={e => onFieldChange('insured_last_name', e.target.value)}
            onBlur={() => onFieldBlur?.('insured_last_name')}
            inputSize='sm'
          />
        </div>
        <div className='space-y-1.5'>
          <FieldLabel>Insured First Name</FieldLabel>
          <Input
            value={form.insured_first_name}
            onChange={e => onFieldChange('insured_first_name', e.target.value)}
            onBlur={() => onFieldBlur?.('insured_first_name')}
            inputSize='sm'
          />
        </div>
        <div className='space-y-1.5'>
          <FieldLabel>Insured Gender</FieldLabel>
          <Select
            value={form.insured_gender}
            onValueChange={v => onFieldChange('insured_gender', v)}
          >
            <SelectTrigger selectSize='sm'>
              <SelectValue placeholder='Select gender' />
            </SelectTrigger>
            <SelectContent>
              {GENDER_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className='space-y-1.5'>
          <FieldLabel error={hasError('insured_ssn')}>Insured SSN</FieldLabel>
          <Input
            value={form.insured_ssn}
            onChange={e =>
              onFieldChange('insured_ssn', formatSSN(e.target.value))
            }
            onBlur={() => onFieldBlur?.('insured_ssn')}
            placeholder='XXX-XX-XXXX'
            inputSize='sm'
          />
        </div>
        <div className='space-y-1.5'>
          <FieldLabel>Insured DOB</FieldLabel>
          <DatePicker
            date={form.insured_dob}
            onDateChange={d => onFieldChange('insured_dob', d)}
            datePickerSize='sm'
            typable
            className='w-full'
          />
        </div>
        <div className='space-y-1.5'>
          <FieldLabel>Insured Phone</FieldLabel>
          <Input
            value={form.insured_phone}
            onChange={e => onFieldChange('insured_phone', e.target.value)}
            onBlur={() => onFieldBlur?.('insured_phone')}
            inputSize='sm'
          />
        </div>
      </div>

      {/* Employer / Plan */}
      <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
        <div className='space-y-1.5'>
          <FieldLabel>Insured Employer</FieldLabel>
          <Input
            value={form.insured_employer}
            onChange={e => onFieldChange('insured_employer', e.target.value)}
            onBlur={() => onFieldBlur?.('insured_employer')}
            inputSize='sm'
          />
        </div>
        <div className='space-y-1.5'>
          <FieldLabel>Plan Name</FieldLabel>
          <Input
            value={form.plan_name}
            onChange={e => onFieldChange('plan_name', e.target.value)}
            onBlur={() => onFieldBlur?.('plan_name')}
            inputSize='sm'
          />
        </div>
      </div>

      {/* Insured address */}
      <div className='grid grid-cols-1 gap-3'>
        <div className='space-y-1.5'>
          <FieldLabel>Insured Address</FieldLabel>
          <Input
            value={form.insured_address}
            onChange={e => onFieldChange('insured_address', e.target.value)}
            onBlur={() => onFieldBlur?.('insured_address')}
            inputSize='sm'
          />
        </div>
      </div>
      <div className='grid grid-cols-1 gap-3 md:grid-cols-3'>
        <div className='space-y-1.5'>
          <FieldLabel>City</FieldLabel>
          <Input
            value={form.insured_city}
            onChange={e => onFieldChange('insured_city', e.target.value)}
            onBlur={() => onFieldBlur?.('insured_city')}
            inputSize='sm'
          />
        </div>
        <div className='space-y-1.5'>
          <FieldLabel>State</FieldLabel>
          <StateCombobox
            value={form.insured_state}
            onChange={v => onFieldChange('insured_state', v)}
          />
        </div>
        <div className='space-y-1.5'>
          <FieldLabel>ZIP</FieldLabel>
          <Input
            value={form.insured_zip}
            onChange={e => onFieldChange('insured_zip', e.target.value)}
            onBlur={() => onFieldBlur?.('insured_zip')}
            inputSize='sm'
          />
        </div>
      </div>

      {/* Relation / Group / Policy */}
      <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
        <div className='space-y-1.5'>
          <FieldLabel>Relation to Insured</FieldLabel>
          <Select
            value={form.relation_to_insured}
            onValueChange={v =>
              onFieldChange('relation_to_insured', v as RelationToInsured)
            }
          >
            <SelectTrigger selectSize='sm'>
              <SelectValue placeholder='Select relation' />
            </SelectTrigger>
            <SelectContent>
              {RELATION_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {!hidden.has('policy_number') && (
          <div className='space-y-1.5'>
            <FieldLabel>Policy Number</FieldLabel>
            <Input
              value={form.policy_number}
              onChange={e => onFieldChange('policy_number', e.target.value)}
              onBlur={() => onFieldBlur?.('policy_number')}
              inputSize='sm'
            />
          </div>
        )}
        <div className='space-y-1.5'>
          <FieldLabel>Group Name</FieldLabel>
          <Input
            value={form.group_name}
            onChange={e => onFieldChange('group_name', e.target.value)}
            onBlur={() => onFieldBlur?.('group_name')}
            inputSize='sm'
          />
        </div>
        <div className='space-y-1.5'>
          <FieldLabel>Group Number</FieldLabel>
          <Input
            value={form.group_number}
            onChange={e => onFieldChange('group_number', e.target.value)}
            onBlur={() => onFieldBlur?.('group_number')}
            inputSize='sm'
          />
        </div>
      </div>

      {/* Effective dates */}
      <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
        <div className='space-y-1.5'>
          <FieldLabel error={hasError('effective_start')}>
            Effective Start
          </FieldLabel>
          <DatePicker
            date={form.effective_start}
            onDateChange={d => onFieldChange('effective_start', d)}
            datePickerSize='sm'
            typable
            className='w-full'
            error={hasError('effective_start')}
          />
        </div>
        <div className='space-y-1.5'>
          <FieldLabel error={hasError('effective_end')}>
            Effective End
          </FieldLabel>
          <DatePicker
            date={form.effective_end}
            onDateChange={d => onFieldChange('effective_end', d)}
            datePickerSize='sm'
            typable
            className='w-full'
            error={hasError('effective_end')}
          />
        </div>
      </div>

      {/* Financial */}
      <div className='grid grid-cols-1 gap-3 md:grid-cols-3'>
        <div className='space-y-1.5'>
          <FieldLabel>Deductible ($)</FieldLabel>
          <Input
            value={form.deductible}
            onChange={e => onFieldChange('deductible', e.target.value)}
            onBlur={() => onFieldBlur?.('deductible')}
            type='number'
            min='0'
            step='0.01'
            inputSize='sm'
          />
        </div>
        <div className='space-y-1.5'>
          <FieldLabel>Coverage (%)</FieldLabel>
          <Input
            value={form.coverage_percent}
            onChange={e => onFieldChange('coverage_percent', e.target.value)}
            onBlur={() => onFieldBlur?.('coverage_percent')}
            type='number'
            min='0'
            max='100'
            step='0.01'
            inputSize='sm'
          />
        </div>
        <div className='space-y-1.5'>
          <FieldLabel>Co-Pay ($)</FieldLabel>
          <Input
            value={form.copay_amount}
            onChange={e => onFieldChange('copay_amount', e.target.value)}
            onBlur={() => onFieldBlur?.('copay_amount')}
            type='number'
            min='0'
            step='0.01'
            inputSize='sm'
          />
        </div>
      </div>

      {/* Checkboxes */}
      <div className='flex flex-col gap-3'>
        <div className='flex items-center gap-2'>
          <Checkbox
            id={`${idPrefix}assignment_of_benefits`}
            checked={form.assignment_of_benefits}
            onCheckedChange={v => onFieldChange('assignment_of_benefits', !!v)}
            checkboxSize='sm'
          />
          <Label
            htmlFor={`${idPrefix}assignment_of_benefits`}
            className='text-sm cursor-pointer'
          >
            Assignment of Benefits (AOB)
          </Label>
        </div>
        <div className='flex items-center gap-2'>
          <Checkbox
            id={`${idPrefix}insured_sig`}
            checked={form.insured_signature_on_file}
            onCheckedChange={v =>
              onFieldChange('insured_signature_on_file', !!v)
            }
            checkboxSize='sm'
          />
          <Label
            htmlFor={`${idPrefix}insured_sig`}
            className='text-sm cursor-pointer'
          >
            Insured Signature on File
          </Label>
        </div>
      </div>
    </div>
  );
};
